import React, { useCallback, useEffect, useState } from 'react';
import { IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner';
import { forEach, isNil } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useStartTableSessionMutation } from '../store/table/tableApi';
import { setAuthState } from '../store/auth/authSlice';
import { useLazyGetRestaurantByIdQuery } from '../store/restaurant/restaurantApi';

export const QrScanner = (): JSX.Element => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [startTableSession] = useStartTableSessionMutation();
	const [getRestaurant] = useLazyGetRestaurantByIdQuery();
	const [hasError, setHasError] = useState<boolean>(false);
	const searchParams = new URLSearchParams(document.location.search);
	const tableIdParam = searchParams.get('tableId');
	const restaurantIdParam = searchParams.get('restaurantId');
	const isGeneralNoTableQrParam = searchParams.get('isGeneralNoTableQr');

	const startTableSessionHandler = useCallback(
		async (tableId: string): Promise<string> => {
			try {
				const { id, restaurantId } = await startTableSession({ tableId }).unwrap();

				dispatch(
					setAuthState({
						tableSessionId: id,
						restaurantId,
					})
				);

				return id;
			} catch (err: any) {
				// TODO: This needs to relay on a specific error type or code
				if (err?.data?.message === 'This table already has a live session') {
					if (!isNil(err?.data?.tableSessionId) && !isNil(err?.data?.restaurantId)) {
						dispatch(
							setAuthState({
								tableSessionId: err?.data?.tableSessionId,
								restaurantId: err?.data?.restaurantId,
							})
						);

						return err?.data?.tableSessionId;
					}

					console.error('Table already has a live session - but Table session ID or restaurant ID is missing in the response', err);
				} else {
					setHasError(true);
				}
			}
		},
		[dispatch, startTableSession]
	);

	const handleQrData = useCallback(
		async ({ restaurantId, tableId, isGeneralNoTableQr }: { restaurantId: string; tableId: string; isGeneralNoTableQr: boolean }): Promise<void> => {
			if (!isNil(restaurantId)) {
				if (isGeneralNoTableQr) {
					dispatch(setAuthState({ tableSessionId: null, restaurantId }));
					navigate('/session/menu');
				} else if (!isNil(tableId)) {
					const restaurant = await getRestaurant(restaurantId).unwrap();

					if (restaurant?.allowTableOrders) {
						const tableSessionId = await startTableSessionHandler(tableId);

						dispatch(setAuthState({ tableSessionId, restaurantId }));

						navigate('/session/menu');
					} else {
						console.error('Restaurant does not allow user table sessions');
					}
				}
			} else {
				console.error('No table id found in the QR code');
			}
		},
		[dispatch, getRestaurant, navigate, startTableSessionHandler]
	);

	useEffect(() => {
		if (!isNil(restaurantIdParam)) {
			handleQrData({ restaurantId: restaurantIdParam, tableId: tableIdParam, isGeneralNoTableQr: isGeneralNoTableQrParam === 'true' });
		}
	}, [tableIdParam, startTableSessionHandler, restaurantIdParam, handleQrData, isGeneralNoTableQrParam]);

	const handleScan = async (result: IDetectedBarcode[]): Promise<void> => {
		let restaurantId: string = null;
		let tableId: string = null;
		let isGeneralNoTableQr = false;

		forEach(result, (barcode) => {
			if (!isNil(barcode.rawValue)) {
				try {
					const barcodeValueToJson = JSON.parse(barcode.rawValue);

					if (!isNil(barcodeValueToJson?.restaurantId)) {
						restaurantId = barcodeValueToJson.restaurant;
					}

					if (!isNil(barcodeValueToJson?.table)) {
						tableId = barcodeValueToJson.table;
					}

					if (barcodeValueToJson?.type === 'general_no_table') {
						isGeneralNoTableQr = true;
					}
				} catch (e) {
					console.error('Error parsing barcode value to JSON', e);
				}
			}
		});

		handleQrData({ restaurantId, tableId, isGeneralNoTableQr });
	};

	return (
		<div className="qrScannerContainer">
			<div className="errorMessage">{hasError && <span>Something went wrong, please try again</span>}</div>
			<Scanner onScan={handleScan} sound={false} />
		</div>
	);
};
